import * as msal from '@azure/msal-browser'
import {makeAutoObservable} from 'mobx'
import {AccountInfo} from '@azure/msal-common'
import {setInterval} from 'timers'
import {config, loaderStore} from '../index'
import {LocalStorageClear, LocalStorageGet, LocalStorageRemove, LocalStorageSet} from './Helpers'
import {ApiService} from "./ApiService";
import {ApiURL} from "../AppConstants";
import {PromiseCompletion} from "./PromiseCompletion";

type UserInfo = {
    accessToken: string
    account: AccountInfo
}
type CustomerDTO = {
    id: string
    name: string
    admin?: string[]
    subscriptions: {
        id: string
        name: string
    }[]
}
type JwtToken = {
    aio: string;
    amr: string[];
    aud: string;
    exp: number;
    family_name: string;
    given_name: string;
    iat: number;
    ipaddr: string;
    iss: string;
    name: string;
    nbf: number;
    nonce: string;
    oid: string;
    rh: string;
    roles: string[];
    sub: string;
    tid: string;
    unique_name: string;
    upn: string;
    uti: string;
    ver: string;
};

const TOKEN_KEY = 'currentToken'
const USER_KEY = 'currentUser'
const TERM_KEY = 'terms'

export class AuthService {
    msalInstance: msal.PublicClientApplication
    currentToken: string = ''
    currentUser: AccountInfo | null = null
    userInfo: UserInfo | null = null
    customer: CustomerDTO | null = null
    private _tokenRefreshInterval: ReturnType<typeof setInterval> | null = null
    private _allowTerm: boolean = false
    dataLoader: PromiseCompletion = new PromiseCompletion()

    constructor() {
        makeAutoObservable(this)
        this.msalInstance = new msal.PublicClientApplication(config.msal.auth)
    }

    public async init() {
        await this._selectAccount()
    }

    private async _setCurrentUser(userInfo: UserInfo) {
        this._setUser(userInfo.account)
        this._setToken(userInfo.accessToken)
        const companyId = (userInfo.account.idTokenClaims as any)?.extension_Company as string
        if (companyId.length > 1) {
            const {data} = await ApiService.getData<CustomerDTO>(`${ApiURL.Customer}/${companyId}`, null, {completion: this.dataLoader})
            this.customer = data
        }
        try {
            if (!this.isTokenValid) {
                await this.initTokenAutoRefresh()
            }
        } catch (e) {
        }
    }

    public signIn() {
        const loginRequest = {
            scopes: ['openid', ...config.msal.api.b2cScopes],
        }
        loaderStore.showAppLoader()
        this.msalInstance.loginPopup(loginRequest).then(async response => {
            this._handleResponse(response)
        }).catch(error => {
            console.log(error)
            if (error.errorCode === 'multiple_matching_tokens') {
                LocalStorageClear()
            } else {
                this._clearUserData()
            }
        }).finally(() => {
            loaderStore.hideAppLoader()
        })
    }

    public async initTokenAutoRefresh() {
        this.stopTokenAutoRefresh()
        this._tokenRefreshInterval = setInterval(async () => {
            void this._refreshToken()
        }, 30 * 60 * 1000)
        return this._refreshToken()
    }

    public get isTokenValid() {
        return this.currentUser?.idTokenClaims && (new Date((this.currentUser.idTokenClaims as any).exp * 1000).getTime() - new Date().getTime()) > 30 * 60 * 1000
    }

    public stopTokenAutoRefresh() {
        if (this._tokenRefreshInterval !== null) {
            clearInterval(this._tokenRefreshInterval)
        }
    }

    public async signOut(signOutUser?: AccountInfo) {
        const user = signOutUser || this.currentUser
        if (user) {
            const logoutRequest = {
                account: this.msalInstance.getAccountByHomeId(user.homeAccountId),
                postLogoutRedirectUri: window.location.origin,
                onRedirectNavigate: this._clearUserData,
            }
            loaderStore.showAppLoader()
            await this.msalInstance.logoutRedirect(logoutRequest)
        }
    }

    public get isTermsAcceptedForCurrent() {
        if (!this._allowTerm) return true
        if (this.userInfo) {
            const terms = LocalStorageGet<string[]>(TERM_KEY) || []
            return terms.includes(this.userInfo.account.username)
        }
        return true
    }

    public get isAdmin() {
        return (this.currentUser?.idTokenClaims as any)?.extension_Role.includes('Admin')
    }

    public get isSSMMember() {
        return (this.currentUser?.idTokenClaims as any)?.extension_Company === '*'
    }

    public get isCompanyAdmin() {
        return this.isAdmin &&
            (((this.currentUser?.idTokenClaims as any)?.extension_Company as string).length > 1) &&
            ((this.currentUser?.idTokenClaims as any)?.extension_Regions === '*') &&
            ((this.currentUser?.idTokenClaims as any)?.extension_Plants === '*') &&
            ((this.currentUser?.idTokenClaims as any)?.extension_Departments === '*')
    }

    public get hasRecipeManagementSubscription() {
        return this.customer !== null && this.customer.subscriptions.findIndex(s => s.name === 'Recipe Management') !== -1
    }

    public get hasScannerAppAccess() {
        return !!(this.currentUser?.idTokenClaims as any)?.extension_ScannerApp &&
            this.hasRecipeManagementSubscription
    }

    private _handleResponse = (response: any) => {
        if (response !== null) {
            const {account, accessToken} = response
            this.userInfo = {account, accessToken}
            if (this.isTermsAcceptedForCurrent) {
                void this._setCurrentUser(this.userInfo)
            }
        }
    }

    private _setUser(user: AccountInfo | null) {
        this.currentUser = user
        user ? LocalStorageSet(USER_KEY, user) : LocalStorageRemove(USER_KEY)
    }

    private _setToken(token: string) {
        this.currentToken = token
        token ? LocalStorageSet(TOKEN_KEY, token) : LocalStorageRemove(TOKEN_KEY)
    }

    private _clearUserData = () => {
        this.userInfo = null
        this._setUser(null)
        this._setToken('')
    }

    private _parseJwtToken (token: string): JwtToken | null {
        if (!token) return null;

        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64).split('').map((c: string) => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join('')
        );

        return JSON.parse(jsonPayload);
    }

    private _validateToken (token: string) {
        const payload = this._parseJwtToken(token);
        if (!payload)
            return false;
        const nowUtc = Date.now();
        const expireAt = new Date(payload.exp * 1000);
        return expireAt.getTime() > nowUtc;
    };

    private async _selectAccount() {
        const currentAccounts = this.msalInstance.getAllAccounts()
        const token = LocalStorageGet<string>(TOKEN_KEY)
        if (!!currentAccounts.length && token && this._validateToken(token || '')) {
            this.userInfo = {account: currentAccounts[0], accessToken: token || ''}
            if (!this.isTermsAcceptedForCurrent) {
                loaderStore.showAppLoader()
                this._clearUserData()
                await this.signOut(currentAccounts[0])
            } else {
                await this._setCurrentUser(this.userInfo)
            }
        } else {
            this._clearUserData();
        }
    }

    private async _refreshToken() {
        if (this.currentUser) {
            const conf = {
                scopes: ['openid', ...config.msal.api.b2cScopes],
                account: this.currentUser,
                forceRefresh: true,
            }
            try {
                const tokenSilent = await this.msalInstance.acquireTokenSilent(conf)
                this._handleResponse(tokenSilent)
                return tokenSilent
            } catch (silentError) {
                if (silentError instanceof msal.InteractionRequiredAuthError) {
                    try {
                        const tokenPopup = await this.msalInstance.acquireTokenPopup(conf)
                        this._handleResponse(tokenPopup)
                        return tokenPopup
                    } catch (popupError) {
                        this._clearUserData()
                        return popupError
                    }
                }
            }
        }
    }
}
